import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { LoadError } from './ui';
import { shownTaskTitle } from './taskTitle';
import { docText, useDocLang } from './docText';
// DATEDAY: date-only values shown as their calendar day in every timezone.
import { toDisplayDate } from './dateOnly';
import { isWorkflowStepTask } from './workflowStepTask';

const API = '/api';

const APP_STATUS_LABELS = {
  approved:       'Approved: in development',
  rejected:       'Rejected',
  live:           'Live',
  in_testing:     'In Testing: awaiting review',
  in_development: 'Returned to development (KO)',
};

const APP_STATUS_COLORS = {
  approved:       '#065f46',
  rejected:       '#991b1b',
  live:           '#166534',
  in_testing:     '#92400e',
  in_development: '#7f1d1d',
};

// HRPROFILE follow-up: onSelectTask(employeeId, taskType), when given, opens one of the viewer's own
// upload tasks on its upload dialog. Without it the bell behaves exactly as before.
const OPENABLE_TASK_TYPES = new Set(['upload_cv', 'upload_contract', 'upload_job_advertisement',
                                     'upload_background_check_result']);

function Notifications({ onClose, onSelectEmployee, onSelectTask = null, filter = 'all', currentUser }) {
  // WFTASKS: a workflow step task is handed to the shell with the task itself, to open its step;
  // every other task keeps the (employee id, task type) call.
  const openTask = (employeeId, task) => (isWorkflowStepTask(task)
    ? onSelectTask(employeeId, task.task_type, task)
    : onSelectTask(employeeId, task.task_type));
  // HRVISAALL: the label on a visa task HR sees as a copy, in the viewer's language.
  const lang = useDocLang();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // NOTIFCRASHFIX: distinct from `loading`. A failed fetch left `data` at its null default while
  // `loading` still became false, and the code immediately below destructures `data` - a bare
  // `TypeError: Cannot destructure property 'overdue_tasks' of 'null'`. This app has exactly one
  // top-level ErrorBoundary (index.js), so that crash was not contained to this modal, it could
  // take down the whole application below that boundary.
  const [loadFailed, setLoadFailed] = useState(false);
  const [expandedOverdue, setExpandedOverdue] = useState(null);

  const load = () => {
    setLoading(true);
    setLoadFailed(false);
    const params = currentUser ? { caller: currentUser } : {};
    axios.get(`${API}/notifications/check`, { params })
      .then(res => { setData(res.data); setLoading(false); })
      .catch(err => { console.error(err); setLoadFailed(true); setLoading(false); });
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  if (loading) return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <p style={styles.loading}>Checking notifications...</p>
      </div>
    </div>
  );

  // NOTIFCRASHFIX: guarded BEFORE the destructure below, which is the exact line that used to
  // throw. `data` may genuinely be null here (the load failed), so this must return before that
  // line is ever reached, not merely display something confusing after it.
  if (loadFailed || !data) return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <div style={styles.header}>
          <h2 style={styles.title}>Notifications</h2>
          <button style={styles.closeBtn} onClick={onClose}>×</button>
        </div>
        <LoadError message="Could not load your notifications." onRetry={load} />
      </div>
    </div>
  );

  const { overdue_tasks, due_reminders, app_request_notifications = [], summary } = data;

  const showOverdue = filter === 'all' || filter === 'overdue';
  const showReminders = filter === 'all' || filter === 'reminders';
  const showAppReqs = filter === 'all' || filter === 'app_requests';

  const visibleCount = (showOverdue ? summary.overdue_tasks_count : 0) +
                       (showReminders ? summary.reminders_due_count : 0) +
                       (showAppReqs ? (summary.app_requests_count || 0) : 0);

  const title = filter === 'overdue' ? 'Overdue Tasks'
    : filter === 'reminders' ? 'Reminders Due'
    : filter === 'app_requests' ? 'Application Requests'
    : 'Notifications';

  const handleEmployeeClick = (employeeId) => {
    if (onSelectEmployee && employeeId) {
      onClose();
      onSelectEmployee(employeeId);
    }
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>

        <div style={styles.header}>
          <div>
            <h2 style={styles.title}>{title}</h2>
            <p style={styles.subtitle}>
              {visibleCount === 0
                ? 'All clear: nothing requires attention'
                : `${visibleCount} item${visibleCount > 1 ? 's' : ''} require attention`}
            </p>
          </div>
          <button style={styles.closeBtn} onClick={onClose}>✕</button>
        </div>

        {/* Overdue Tasks - grouped by employee */}
        {showOverdue && (
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>
              Overdue Tasks
              {overdue_tasks.length > 0 && <span style={styles.badgeRed}>{overdue_tasks.length}</span>}
            </h3>
            {overdue_tasks.length === 0 ? (
              <p style={styles.empty}>No overdue tasks</p>
            ) : (
              (() => {
                const grouped = overdue_tasks.reduce((acc, t) => {
                  const key = t.employee_id || t.employee_name;
                  if (!acc[key]) acc[key] = { employee_id: t.employee_id, employee_name: t.employee_name, tasks: [] };
                  acc[key].tasks.push(t);
                  return acc;
                }, {});
                const [expanded, setExpanded] = [expandedOverdue, setExpandedOverdue];
                return Object.values(grouped).map(group => (
                  <div key={group.employee_id} style={styles.reminderGroup}>
                    <div
                      style={{...styles.reminderGroupHeader, cursor: 'pointer'}}
                      onClick={() => setExpanded(expanded === group.employee_id ? null : group.employee_id)}
                    >
                      <div style={styles.reminderAvatar}>{group.employee_name.charAt(0)}</div>
                      <div style={styles.reminderGroupInfo}>
                        <p style={styles.reminderGroupName}>{group.employee_name}</p>
                        <p style={{...styles.reminderGroupCount, color: '#e74c3c'}}>
                          {group.tasks.length} overdue task{group.tasks.length > 1 ? 's' : ''}
                          <span style={{fontSize: '11px', color: '#aaa', fontStyle: 'italic'}}>
                            {expanded === group.employee_id ? ' (collapse)' : ' (expand)'}
                          </span>
                        </p>
                      </div>
                      {group.employee_id && (
                        <span
                          style={{...styles.viewHint, cursor: 'pointer'}}
                          onClick={e => { e.stopPropagation(); handleEmployeeClick(group.employee_id); }}
                        >View profile</span>
                      )}
                    </div>
                    {expanded === group.employee_id && (
                      <div style={styles.reminderList}>
                        {group.tasks.map(task => {
                          // WFTASKS: a workflow step task opens that workflow step.
                          const openable = !!onSelectTask && !task.copy && (isWorkflowStepTask(task)
                            || (!!group.employee_id && OPENABLE_TASK_TYPES.has(task.task_type)));
                          return (
                          <div key={task.task_id} style={{ ...styles.reminderItem, ...(openable ? { cursor: 'pointer' } : {}) }}
                            data-testid={openable ? `bell-open-${task.task_type}` : undefined}
                            role={openable ? 'button' : undefined}
                            tabIndex={openable ? 0 : undefined}
                            onClick={openable ? () => { onClose(); openTask(group.employee_id, task); } : undefined}
                            onKeyDown={openable ? (e) => { if (e.key === 'Enter') { onClose(); openTask(group.employee_id, task); } } : undefined}>
                            <span style={{...styles.itemDot, backgroundColor: '#e74c3c'}} />
                            <div style={{flex: 1}}>
                              <p style={styles.itemTitle}>{shownTaskTitle(task, 'task_title')}</p>
                              <p style={styles.itemMeta}>{task.days_overdue} days overdue</p>
                              {/* HRVISAALL: a COO visa task HR receives as a copy, never as HR's work. */}
                              {task.copy && (
                                <p style={styles.itemMeta} data-testid="visa-copy-label">{docText('visaHr.copyLabel', lang)}</p>
                              )}
                            </div>
                            <span style={styles.badgeRed}>Overdue</span>
                          </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ));
              })()
            )}
          </div>
        )}

        {/* Due Reminders - grouped by employee */}
        {showReminders && (
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>
              Reminders Due
              {due_reminders.length > 0 && <span style={styles.badgeOrange}>{due_reminders.length}</span>}
            </h3>
            {due_reminders.length === 0 ? (
              <p style={styles.empty}>No reminders due</p>
            ) : (
              (() => {
                const grouped = due_reminders.reduce((acc, r) => {
                  const key = r.employee_name;
                  if (!acc[key]) acc[key] = { employee_name: r.employee_name, employee_id: r.employee_id, reminders: [] };
                  acc[key].reminders.push(r);
                  return acc;
                }, {});

                return Object.values(grouped).map(group => (
                  <div
                    key={group.employee_name}
                    style={{...styles.reminderGroup, cursor: group.employee_id ? 'pointer' : 'default'}}
                    onClick={() => handleEmployeeClick(group.employee_id)}
                  >
                    <div style={styles.reminderGroupHeader}>
                      <div style={styles.reminderAvatar}>{group.employee_name.charAt(0)}</div>
                      <div style={styles.reminderGroupInfo}>
                        <p style={styles.reminderGroupName}>{group.employee_name}</p>
                        <p style={styles.reminderGroupCount}>
                          {group.reminders.length} reminder{group.reminders.length > 1 ? 's' : ''} due
                        </p>
                      </div>
                      {group.employee_id && <span style={styles.viewHint}>View profile</span>}
                    </div>
                    <div style={styles.reminderList}>
                      {group.reminders.map(reminder => (
                        <div key={reminder.reminder_id} style={styles.reminderItem}>
                          <span style={{...styles.itemDot, backgroundColor: '#f39c12'}} />
                          <div style={{flex: 1}}>
                            <p style={styles.itemTitle}>
                              {reminder.type.replace(/_/g, ' ').toUpperCase()}
                            </p>
                            <p style={styles.itemMeta}>
                              Due {toDisplayDate(reminder.due_date).toLocaleDateString()}
                            </p>
                          </div>
                          <span style={styles.badgeOrange}>Due</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ));
              })()
            )}
          </div>
        )}

        {/* App Request Notifications */}
        {showAppReqs && app_request_notifications.length > 0 && (
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>
              Application Requests
              <span style={styles.badgePurple}>{app_request_notifications.length}</span>
            </h3>
            {app_request_notifications.map((n, i) => (
              <div key={`${n.request_id}-${i}`} style={styles.item}>
                <div style={styles.itemLeft}>
                  <span style={{...styles.itemDot, backgroundColor: APP_STATUS_COLORS[n.status] || '#6b7280'}} />
                  <div>
                    <p style={styles.itemTitle}>{n.request_title}</p>
                    <p style={styles.itemMeta}>{n.message}</p>
                  </div>
                </div>
                <span style={{
                  ...styles.badgePurple,
                  backgroundColor: APP_STATUS_COLORS[n.status] || '#6b7280',
                  fontSize: '10px',
                }}>
                  {APP_STATUS_LABELS[n.status] || n.status}
                </span>
              </div>
            ))}
          </div>
        )}

      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000
  },
  modal: {
    backgroundColor: '#fff', borderRadius: '12px',
    padding: '28px', width: '560px', maxWidth: '90vw',
    maxHeight: '80vh', overflowY: 'auto',
    boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
  },
  header: {
    display: 'flex', justifyContent: 'space-between',
    alignItems: 'flex-start', marginBottom: '24px'
  },
  title: { fontSize: '18px', fontWeight: '600', color: '#1a1a2e', margin: 0 },
  subtitle: { fontSize: '13px', color: '#666', margin: '4px 0 0 0' },
  closeBtn: {
    background: 'none', border: 'none', fontSize: '18px',
    cursor: 'pointer', color: '#666', padding: '0'
  },
  section: { marginBottom: '24px' },
  sectionTitle: {
    fontSize: '14px', fontWeight: '600', color: '#1a1a2e',
    margin: '0 0 12px 0', display: 'flex', alignItems: 'center', gap: '8px',
    textTransform: 'uppercase', letterSpacing: '0.5px'
  },
  item: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '12px 0', borderBottom: '1px solid #eee'
  },
  itemLeft: { display: 'flex', alignItems: 'center', gap: '12px' },
  itemRight: { display: 'flex', alignItems: 'center', gap: '8px' },
  itemDot: {
    width: '8px', height: '8px', borderRadius: '50%',
    backgroundColor: '#e74c3c', flexShrink: 0
  },
  itemTitle: { fontSize: '14px', color: '#1a1a2e', margin: '0 0 2px 0', fontWeight: '500' },
  itemMeta: { fontSize: '12px', color: '#888', margin: 0 },
  viewHint: { fontSize: '12px', color: '#888' },
  badgeRed: {
    backgroundColor: '#e74c3c', color: '#fff',
    borderRadius: '12px', padding: '2px 8px', fontSize: '11px', fontWeight: '500'
  },
  badgeOrange: {
    backgroundColor: '#f39c12', color: '#fff',
    borderRadius: '12px', padding: '2px 8px', fontSize: '11px', fontWeight: '500'
  },
  badgePurple: { backgroundColor: '#5b21b6', color: '#fff', borderRadius: '12px', padding: '2px 8px', fontSize: '11px', fontWeight: '500', whiteSpace: 'nowrap' },
  empty: { fontSize: '14px', color: '#27ae60', margin: 0 },
  loading: { fontSize: '14px', color: '#666', textAlign: 'center', padding: '24px' },
  reminderGroup: {
    border: '1px solid #eee', borderRadius: '8px',
    marginBottom: '12px', overflow: 'hidden'
  },
  reminderGroupHeader: {
    display: 'flex', alignItems: 'center', gap: '12px',
    padding: '12px 16px', backgroundColor: '#f8f9fa'
  },
  reminderAvatar: {
    width: '32px', height: '32px', borderRadius: '50%',
    backgroundColor: '#1a1a2e', color: '#fff',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '14px', fontWeight: '600', flexShrink: 0
  },
  reminderGroupInfo: { flex: 1 },
  reminderGroupName: { fontSize: '14px', fontWeight: '600', color: '#1a1a2e', margin: '0 0 2px 0' },
  reminderGroupCount: { fontSize: '12px', color: '#f39c12', margin: 0, fontWeight: '500' },
  reminderList: { padding: '8px 16px' },
  reminderItem: {
    display: 'flex', alignItems: 'center', gap: '10px',
    padding: '8px 0', borderBottom: '1px solid #f0f0f0'
  }
};

export default Notifications;
